import { useQuery } from "@tanstack/react-query";
import { onAuthStateChanged, User } from "firebase/auth";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { getClient } from "services";
import { getFirebaseAuth } from "services/firebase/auth";

import { isSignedIn as getIsSignedIn } from "seneca-common/features/user/state";

import { getAppVersion } from "features/common/utils/functions/app";
import { getBrowserDetails } from "features/common/utils/functions/browser";

const REFETCH_INTERVAL = 15 * 60 * 1000; // 15mins

export default function useUpdateUserLastVisit({
  initialized
}: {
  initialized: boolean;
}) {
  const appVersion = getAppVersion();
  const browser = getBrowserDetails();
  const auth = getFirebaseAuth();
  const isSignedIn = useSelector(getIsSignedIn);
  const lastVisitTime = new Date().toISOString();

  useEffect(() => {
    const unsubscribeFirebaseEventListener = onAuthStateChanged(
      auth,
      (user: User | null) => {
        if (user?.uid !== auth.currentUser?.uid && !!user) {
          getClient("authenticationService").updateUserLastVisit({
            browser,
            appVersion,
            lastVisitTime
          });
        }
      }
    );
    return unsubscribeFirebaseEventListener;
  }, [appVersion, browser, lastVisitTime, auth]);

  return useQuery({
    queryKey: ["update-user-last-visit"],
    queryFn: async () => {
      await getClient("authenticationService").updateUserLastVisit({
        browser,
        appVersion,
        lastVisitTime
      });

      // react-query complains about returning undefined for queries if we don't explicitly return something
      return null;
    },
    enabled: !!auth.currentUser && initialized && isSignedIn,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: REFETCH_INTERVAL,
    retry: false
  });
}
